import type { ProtocolCallRequestEvent } from "../types";

/**
 * The resolution state of a call request, derived from its event log rather than stored
 * on the protocol: a call request counts as resolved once someone logged a `resolved`
 * event, and stays open no matter how many `unreachable` attempts came before it.
 */
export interface ProtocolCallRequestStatus {
	isResolved: boolean;
	resolvedEvent: ProtocolCallRequestEvent | null;
	attemptCount: number;
}

/**
 * This function derives the resolution state of a call request from its event log.
 * @param events - The call request's events, in any order.
 * @returns Whether the request is resolved, the event that resolved it, and how many
 * failed callback attempts were logged.
 */
export function getProtocolCallRequestStatus(
	events: ProtocolCallRequestEvent[] | null | undefined,
): ProtocolCallRequestStatus {
	let resolvedEvent: ProtocolCallRequestEvent | null = null;
	let attemptCount = 0;

	for (const event of events ?? []) {
		if (event.type === "unreachable") {
			attemptCount += 1;
			continue;
		}
		if (event.type !== "resolved") continue;
		if (!resolvedEvent) {
			resolvedEvent = event;
			continue;
		}
		if (event.createdAt > resolvedEvent.createdAt) {
			resolvedEvent = event;
		}
	}

	return {
		isResolved: !!resolvedEvent,
		resolvedEvent,
		attemptCount,
	};
}
